import { ArrowLeft, ChevronDown, Copy, EyeOff, Search, Send } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { adminCapabilities } from '../auth/permissions'
import { normalizeReportStatus } from '../../../../packages/validation/admin'
import { addReportNote, fetchReportNotes, formatDateTime, saveReportReply, updateReportStatus } from '../lib/data'
import { useReportTriage } from '../lib/reportTriageStore'

const STATUS_LABELS = { all: '전체 상태', new: '신규', reviewing: '검토 중', resolved: '해결', rejected: '반려' }
const TYPE_LABELS = { all: '전체 유형', app: '앱 오류', shuttle: '셔틀·교통', meal: '학식', facility: '시설', timetable: '시간표', etc: '기타' }
const maskedUid = value => value ? `…${String(value).slice(-6)}` : null

export default function ReportsPage() {
  const auth = useAdminAuth()
  const { canWrite } = adminCapabilities(auth.role)
  const navigate = useNavigate()
  const { reportId } = useParams()
  const [params, setParams] = useSearchParams()
  const [items, setItems] = useState([])
  const [notes, setNotes] = useState([])
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [replyText, setReplyText] = useState('')
  const [replyBusy, setReplyBusy] = useState(false)
  const [replyError, setReplyError] = useState('')
  const [replySuccess, setReplySuccess] = useState(false)
  const [isEditingReply, setIsEditingReply] = useState(false)
  const [syncedReplyReportId, setSyncedReplyReportId] = useState(null)
  const [idCopied, setIdCopied] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const { items: reportItems, ready, error: reportError, newCount, countReady } = useReportTriage()
  const status = params.get('status') || 'all'; const type = params.get('type') || 'all'; const search = params.get('q') || ''

  useEffect(() => { setItems(reportItems); setLoading(!ready); setError(reportError ? reportError.message : '') }, [reportItems, ready, reportError])
  const selected = items.find(item => item.id === reportId) || null
  useEffect(() => { let active = true; if (!reportId) { setNotes([]); return undefined } fetchReportNotes(reportId).then(data => active && setNotes(data)).catch(err => active && setError(err.message)); return () => { active = false } }, [reportId])
  // selected가 갱신될 때마다(우리 자신의 답변 저장 포함) 다시 동기화하면 방금 띄운 성공 메시지가
  // 곧바로 지워진다 — 실제로 다른 제보로 전환했을 때만 textarea/성공 상태를 리셋한다.
  useEffect(() => {
    if (selected && selected.id !== syncedReplyReportId) {
      setReplyText(selected.adminReply || ''); setReplyError(''); setReplySuccess(false)
      setIsEditingReply(false)
      setSyncedReplyReportId(selected.id); setNotesOpen(false)
    }
  }, [selected, syncedReplyReportId])
  const filtered = useMemo(() => items.filter(item => (status === 'all' || normalizeReportStatus(item.status) === status) && (type === 'all' || item.type === type) && (!search || `${item.title || ''} ${item.content || ''}`.toLowerCase().includes(search.toLowerCase()))), [items, search, status, type])

  function setFilter(key, value) { const next = new URLSearchParams(params); value ? next.set(key, value) : next.delete(key); setParams(next, { replace: true }) }
  function select(id) { navigate({ pathname: `/reports/${id}`, search: params.toString() }) }
  async function changeStatus(nextStatus) {
    if (!selected || busy) return
    setBusy(true); setError('')
    try { await updateReportStatus(selected, nextStatus, auth.user.uid); setItems(current => current.map(item => item.id === selected.id ? { ...item, status: nextStatus === 'new' ? 'pending' : nextStatus } : item)) } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function submitNote(event) {
    event.preventDefault(); if (!selected || !note.trim() || busy) return
    setBusy(true); setError('')
    try { const id = await addReportNote(selected.id, note, auth.user.uid); setNotes(current => [{ id, content: note.trim(), actorUid: auth.user.uid, createdAt: new Date() }, ...current]); setNote('') } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function submitReply(event) {
    event.preventDefault(); if (!selected || !replyText.trim() || replyBusy) return
    setReplyBusy(true); setReplyError(''); setReplySuccess(false)
    try {
      const trimmed = replyText.trim()
      await saveReportReply(selected.id, trimmed)
      const now = new Date()
      setItems(current => current.map(item => item.id === selected.id ? { ...item, adminReply: trimmed, repliedAt: now, repliedBy: auth.user.uid } : item))
      setReplySuccess(true)
      setIsEditingReply(false)
    } catch (err) { setReplyError(err.message) } finally { setReplyBusy(false) }
  }
  function startEditReply() {
    setReplyText(selected.adminReply || ''); setReplyError(''); setReplySuccess(false); setIsEditingReply(true)
  }
  function cancelEditReply() {
    setReplyText(selected.adminReply || ''); setReplyError(''); setIsEditingReply(false)
  }
  function copyReportId(id) {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(id).catch(() => {})
    setIdCopied(true)
    setTimeout(() => setIdCopied(false), 1500)
  }
  function replyStatusText(report) {
    if (replyBusy) return '저장 중…'
    if (replyError) return replyError
    if (replySuccess) return '답변이 저장되었습니다.'
    if (report.repliedAt) return `최근 저장 ${formatDateTime(report.repliedAt)}${report.repliedBy ? ` · ${maskedUid(report.repliedBy)}` : ''}`
    return '아직 저장된 답변이 없습니다.'
  }

  return (
    <>
      <header className="page-header"><div><p className="eyebrow">운영</p><h1>제보</h1><p>새 제보를 확인하고 처리 상태와 답변을 관리합니다.</p><div className="report-triage-summary" aria-label={`신규 ${countReady ? newCount : 0}건`}><span>신규 <strong>{countReady ? newCount : 0}</strong></span><span>처리중 <strong>{items.filter(item => normalizeReportStatus(item.status) === 'reviewing').length}</strong></span><span>종료 <strong>{items.filter(item => ['resolved', 'rejected'].includes(normalizeReportStatus(item.status))).length}</strong></span></div></div></header>
      {error && <div className="error-state">{error}</div>}
      <section className={`panel split-view ${selected ? 'has-selection' : ''}`}>
        <div className="split-list"><div className="toolbar"><label className="search-field"><Search size={16} /><input className="input" aria-label="제보 검색" value={search} placeholder="제보 내용 검색" onChange={e => setFilter('q', e.target.value)} /></label><select className="select" aria-label="제보 상태" value={status} onChange={e => setFilter('status', e.target.value)}>{Object.entries(STATUS_LABELS).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select><select className="select" aria-label="제보 유형" value={type} onChange={e => setFilter('type', e.target.value)}>{Object.entries(TYPE_LABELS).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></div>
          <div className="split-list-items">{loading ? <div className="empty-state">제보를 불러오는 중…</div> : filtered.length === 0 ? <div className="empty-state">조건에 맞는 제보가 없습니다.</div> : filtered.map(item => <button type="button" key={item.id} className={`split-item ${item.id === reportId ? 'active' : ''}`} onClick={() => select(item.id)}><div className="split-item-top"><strong>{item.title || TYPE_LABELS[item.type] || '사용자 제보'}</strong><span className={`status-badge ${normalizeReportStatus(item.status)}`}>{STATUS_LABELS[normalizeReportStatus(item.status)]}</span></div><p>{item.content}</p><small className="muted">{formatDateTime(item.createdAt)}</small></button>)}</div>
        </div>
        <article className="detail-panel">{!selected ? <div className="empty-state">왼쪽 목록에서 확인할 제보를 선택하세요.</div> : <>
          <div className="report-topbar">
            <button className="icon-button" type="button" onClick={() => navigate({ pathname: '/reports', search: params.toString() })} aria-label="목록으로"><ArrowLeft size={18} /></button>
            <div className="report-topbar-info">
              <div className="report-topbar-title-row">
                <span className={`status-badge ${normalizeReportStatus(selected.status)}`}>{STATUS_LABELS[normalizeReportStatus(selected.status)]}</span>
                <h2>{selected.title || TYPE_LABELS[selected.type] || '사용자 제보'}</h2>
              </div>
              <div className="report-topbar-meta">
                <span className="report-meta-strong">{formatDateTime(selected.createdAt)}</span>
                <span className="report-meta-strong">{TYPE_LABELS[selected.type] || selected.type}</span>
                <button type="button" className="report-id-chip" onClick={() => copyReportId(selected.id)} title={selected.id} aria-label={`제보 ID 복사: ${selected.id}`}>
                  <span>{selected.id}</span>
                  <Copy size={11} />
                </button>
                {idCopied && <span className="report-id-copied">복사됨</span>}
              </div>
            </div>
          </div>

          <div className="report-block report-block--primary">
            <h3>제보 내용</h3>
            <p className="report-content-text">{selected.content}</p>
          </div>

          <div className="report-block report-block--primary">
            <h3>처리 상태</h3>
            {normalizeReportStatus(selected.status) === 'new' && <p className="report-triage-hint">신규 제보입니다. 상태를 <strong>검토 중</strong>으로 변경하면 확인 처리됩니다.</p>}
            {canWrite ? <select className="select report-status-select" value={normalizeReportStatus(selected.status)} disabled={busy} onChange={e => changeStatus(e.target.value)}>{Object.entries(STATUS_LABELS).filter(([value]) => value !== 'all').map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select> : <span className={`status-badge ${normalizeReportStatus(selected.status)}`}>{STATUS_LABELS[normalizeReportStatus(selected.status)]}</span>}
          </div>

          <div className="report-block report-block--primary">
            <div className="report-reply-card">
              <div className="report-reply-head">
                <h3>사용자 답변</h3>
                <span className={`status-badge ${selected.adminReply ? 'resolved' : 'pending'}`}>{selected.adminReply ? '답변 완료' : '답변 필요'}</span>
              </div>
              {selected.adminReply && !isEditingReply ? (
                <div className="report-reply-readonly">
                  <p className="report-reply-text">{selected.adminReply}</p>
                  <div className="report-reply-footer">
                    <span className="report-reply-status">{selected.repliedAt ? `최근 저장 ${formatDateTime(selected.repliedAt)}${selected.repliedBy ? ` · ${maskedUid(selected.repliedBy)}` : ''}` : ''}</span>
                    {canWrite && <button className="button secondary" type="button" onClick={startEditReply}>수정</button>}
                  </div>
                </div>
              ) : (
                canWrite ? <form onSubmit={submitReply}>
                  <textarea className="textarea" value={replyText} onChange={e => { setReplyText(e.target.value); setReplySuccess(false) }} placeholder="사용자에게 공개할 답변을 입력하세요" maxLength={5000} disabled={replyBusy} />
                  <div className="report-reply-footer">
                    <span className={`report-reply-status ${replyError ? 'is-error' : replySuccess ? 'is-success' : ''}`}>{replyStatusText(selected)}</span>
                    <div className="report-reply-actions">
                      {selected.adminReply && <button className="button secondary" type="button" onClick={cancelEditReply} disabled={replyBusy}>취소</button>}
                      <button className="button primary" type="submit" disabled={replyBusy || !replyText.trim()}><Send size={15} />{replyBusy ? '저장 중…' : selected.adminReply ? '수정 저장' : '답변 저장'}</button>
                    </div>
                  </div>
                </form> : <p className="muted">저장된 답변이 없습니다.</p>
              )}
            </div>
          </div>

          <div className="report-block report-block--secondary">
            <h3>제보자 정보</h3>
            <p className="reporter-line">
              <span className="reporter-line-item">{selected.isAnonymous === true ? <><EyeOff size={12} />익명 제보</> : '실명 제보'}</span>
              <span className="reporter-line-item">연락처 {selected.contact || '제공하지 않음'}</span>
              {selected.isAnonymous !== true && <span className="reporter-line-item">UID {selected.userId ? maskedUid(selected.userId) : '정보 없음'}</span>}
            </p>
          </div>

          <div className="report-block report-block--secondary">
            <button type="button" className={`report-accordion-toggle ${notesOpen ? 'open' : ''}`} onClick={() => setNotesOpen(value => !value)} aria-expanded={notesOpen}>
              <span>관리자 메모 <span className="report-note-badge">내부용</span></span>
              <ChevronDown size={16} className="chevron" />
            </button>
            {notesOpen && (
              <div className="report-accordion-body">
                {canWrite && <form onSubmit={submitNote}><textarea className="textarea report-note-textarea" value={note} onChange={e => setNote(e.target.value)} placeholder="사용자에게 노출되지 않는 내부 메모" maxLength={2000} /><button className="button primary" type="submit" disabled={busy || !note.trim()}><Send size={15} />메모 저장</button></form>}
                <div className="data-list">{notes.map(item => <div className="data-row" key={item.id}><div><strong>{item.content}</strong><small>{item.actorUid}</small></div><time>{formatDateTime(item.createdAt)}</time></div>)}</div>
              </div>
            )}
          </div>
        </>}</article>
      </section>
    </>
  )
}
